<script>
  import Pagination from '@/components/pagination/index.vue';
  import BaseListLayout from '@/views/layout/components/BaseListLayout.vue';
  import { myMixin } from '@/mixins/mixins.js';
  import { downloadAffiliateBilling, getAffiliateBillingList } from 'api/billing/affiliateBilling';
  import { handleSortItem } from 'utils/utils';
  import CheckboxWatcher from '@/mixins/checkboxWatcher';
  import { formatDate } from 'utils/dateutils';
  import GroupSelect from '@/components/GroupSelect/index.vue';
  import AffiliateBillingBaseForm from '@/views/billing/forms/AffiliateBillingBaseForm.vue';
  import { billingEnumMixins } from '@/mixins/billingEnumMixins';
  import { financeInfoMixins } from '@/mixins/financeInfoMixins';
  import { roleUserMixins } from '@/mixins/roleUserMixins';
  import { numToPercentStr } from 'utils/string';

  const defaultAffiliateForm = {
    id: null,
    businessModule: null,
    companyPlatform: null,
    affId: null,
    affName: null,
    bdId: null,
    bdName: null,
    amId: null,
    amName: null,
    affRevenue: null,
    affConfirmedAmount: null,
    affEntity: null,
    paymentStatus: null,
    deductionAmount: null,
    financePaymentAmount: null,
    checkedTime: null,
    paymentTime: null,
    reconciliationProgress: null,
    affBillingAppStatus: null,
    paymentMergeStatus: null,
    remark: null,
  };

  export default {
    name: 'AffiliateBilling',
    components: { GroupSelect, AffiliateBillingBaseForm, BaseListLayout, Pagination },
    mixins: [myMixin, CheckboxWatcher, billingEnumMixins, financeInfoMixins, roleUserMixins],
    data() {
      return {
        groupItemCheck: {
          affiliateCheck: false,
          companyPlatformCheck: false,
          bdCheck: false,
          amCheck: false,
          affEntityCheck: false,
          paymentStatusCheck: false,
          reconciliationProgressCheck: false,
        },
        dialogVisible: false,
        dialogType: 'new',
        editData: Object.assign({}, defaultAffiliateForm),
        total: 0,
        listLoading: false,
        dataList: [],
        filter: {
          page: 1,
          pageSize: 10,
          businessModule: null,
          affId: null,
          affCompanyPlatform: null,
          bdName: null,
          amName: null,
          affEntity: null,
          paymentStatus: null,
          reconciliationProgress: null,
          recordDateRange: [formatDate(new Date(), 'yyyy-MM'), formatDate(new Date(), 'yyyy-MM')],
          checkedTimeRange: null,
          paymentTimeRange: null,
          paymentMergeStatus: null,
          affBillingAppStatus: null,
          sortItems: [],
          groupItems: [],
        },
      };
    },
    created() {
      this.filter.groupItems.push('record_date');
    },
    mounted() {
      this.handleSearch();
      this.checkboxConfig.targetArray = this.filter.groupItems;
      this.checkboxConfig.searchFunc = this.handleSearch;
    },
    methods: {
      numToPercentStr,
      handleSizeChange(size) {
        this.filter.pageSize = size;
        this.handleSearch(1);
      },
      handleCurrentChange(currentPage) {
        this.filter.page = currentPage;
        this.handleSearch(currentPage);
      },
      handleDownload() {
        downloadAffiliateBilling(this.filter);
      },
      handleSelectAll() {
        Object.keys(this.groupItemCheck).forEach((key) => {
          this.$set(this.groupItemCheck, key, true);
        });
        this.handleSelectAllCb();
      },
      handleUnSelectAll() {
        Object.keys(this.groupItemCheck).forEach((key) => {
          this.$set(this.groupItemCheck, key, false);
        });
        this.handleUnSelectAllCb();
      },
      handleSort(columnInfo) {
        const sortItem = handleSortItem(columnInfo);
        if (sortItem === '') {
          this.filter.sortItems = [];
          this.handleSearch(1);
          return;
        }
        this.filter.sortItems = [];
        this.filter.sortItems.push(sortItem);
        this.handleSearch(1);
      },
      handleSearch(page) {
        this.listLoading = true;
        if (page) {
          this.filter.page = page;
        }
        getAffiliateBillingList(this.filter)
          .then((res) => {
            if (res) {
              const result = res.result;
              this.dataList = result.dataList;
              this.total = res.total;
            }
          })
          .catch(() => {})
          .finally(() => {
            this.listLoading = false;
          });
      },
      affiliateDetailClick(row) {
        const { href } = this.$router.resolve({
          path: '/affiliate/detail',
          query: {
            affiliateId: row.affId,
          },
        });
        window.open(href, '_blank');
      },
    },
  };
</script>

<template>
  <base-list-layout :title="'Affiliate Billing'">
    <template #search>
      <el-form :inline="true" :model="filter" label-width="170px" ref="searchForm">
        <el-form-item label="Date">
          <div class="row-multi-item">
            <el-date-picker
              v-model="filter.recordDateRange"
              format="yyyy-MM"
              value-format="yyyy-MM"
              type="monthrange"
              clearable
              style="width:260px;"
              align="right"
              unlink-panels
              range-separator="to"
              start-placeholder="开始日期"
              end-placeholder="结束日期"
              :picker-options="monthPickerOptions"
            />
          </div>
        </el-form-item>

        <el-form-item label="Affiliate">
          <div class="row-multi-item">
            <group-select
              v-model="filter.affId"
              :data-source="affiliatesGroupList"
              :loading="affiliateLoading"
              clearable
              filterable
              reserve-keyword
              placeholder="please select"
            />
            <el-checkbox v-model="groupItemCheck.affiliateCheck" name="affiliate_id" />
          </div>
        </el-form-item>
        <el-form-item label="Company Platform">
          <div class="row-multi-item">
            <el-select
              v-model="filter.affCompanyPlatform"
              filterable
              clearable
              @change="handleCompanyPlatformChange(filter.affCompanyPlatform)"
            >
              <el-option
                v-for="item in companyPlatformList"
                :key="item"
                :label="item"
                :value="item"
              />
            </el-select>
            <el-checkbox
              v-model="groupItemCheck.companyPlatformCheck"
              name="aff_company_platform"
            />
          </div>
        </el-form-item>
        <el-form-item label="Aff Entity">
          <div class="row-multi-item">
            <el-select v-model="filter.affEntity" ref="companyEntityOptions" filterable clearable>
              <el-option
                v-for="item in specificCompanyEntity"
                :key="item.id"
                :label="item.companyEntity"
                :value="item.companyEntity"
              />
            </el-select>
            <el-checkbox v-model="groupItemCheck.affEntityCheck" name="fbpa.aff_entity" />
          </div>
        </el-form-item>
        <el-form-item label="BD">
          <div class="row-multi-item">
            <el-select v-model="filter.bdName" filterable clearable>
              <el-option
                v-for="item in allBdList"
                :key="item.value"
                :label="item.label"
                :value="item.label"
              />
            </el-select>
            <el-checkbox v-model="groupItemCheck.bdCheck" name="aff_bd" />
          </div>
        </el-form-item>
        <el-form-item label="AM">
          <div class="row-multi-item">
            <el-select v-model="filter.amName" filterable clearable>
              <el-option
                v-for="item in allAmList"
                :key="item.value"
                :label="item.label"
                :value="item.label"
              />
            </el-select>
            <el-checkbox v-model="groupItemCheck.amCheck" name="affiliate_am" />
          </div>
        </el-form-item>
        <el-form-item label="Reconciliation Progress">
          <div class="row-multi-item">
            <el-select v-model="filter.reconciliationProgress" filterable clearable>
              <el-option
                v-for="item in reconciliationProgressList"
                :key="item.value"
                :label="item.label"
                :value="item.value"
              />
            </el-select>
            <el-checkbox
              v-model="groupItemCheck.reconciliationProgressCheck"
              name="aff_reconciliation_progress"
            />
          </div>
        </el-form-item>
        <el-form-item label="Payment Status">
          <div class="row-multi-item">
            <el-select v-model="filter.paymentStatus" filterable clearable>
              <el-option
                v-for="item in affHedgeAppPaymentStatusList"
                :key="item.value"
                :label="item.label"
                :value="item.value"
              />
            </el-select>
            <el-checkbox v-model="groupItemCheck.paymentStatusCheck" name="payment_status" />
          </div>
        </el-form-item>
        <el-form-item label="Payment Time">
          <el-date-picker
            v-model="filter.paymentTimeRange"
            type="datetimerange"
            value-format="yyyy-MM-dd HH:mm:ss"
            range-separator="至"
            start-placeholder="开始日期"
            end-placeholder="结束日期"
            :picker-options="datetimeRangePickerOptions"
          />
        </el-form-item>
        <el-form-item label="Aff Billing Application Status" label-width="200px">
          <div class="row-multi-item">
            <el-select v-model="filter.affBillingAppStatus" filterable clearable>
              <el-option
                v-for="item in affBillingAppStatusList"
                :key="item.value"
                :label="item.label"
                :value="item.value"
              />
            </el-select>
          </div>
        </el-form-item>
      </el-form>
    </template>
    <template #search-actions>
      <el-button type="primary" icon="el-icon-search" @click="handleSearch(1)">Search</el-button>
      <el-button type="primary" icon="el-icon-download" @click="handleDownload">
        Download Excel
      </el-button>
      <el-button type="primary" @click="handleSelectAll">SelectAll</el-button>
      <el-button type="primary" @click="handleUnSelectAll">UnSelectAll</el-button>
    </template>
    <template #table>
      <el-table
        v-loading="listLoading"
        ref="tableRef"
        :data="dataList"
        highlight-current-row
        @sort-change="handleSort"
      >
        <el-table-column
          label="Date"
          prop="recordDate"
          fixed="left"
          align="center"
          width="120"
          sortable="custom"
          key="recordDate"
        />
        <el-table-column
          label="Affiliate ID"
          prop="affId"
          align="center"
          width="170"
          key="affId"
          v-if="groupItemCheck.affiliateCheck"
        >
          <template slot-scope="scope">
            <span class="cor337ab7" @click="affiliateDetailClick(scope.row)">{{
              scope.row.affId
            }}</span>
          </template>
        </el-table-column>
        <el-table-column
          label="Affiliate Name"
          prop="affName"
          align="center"
          width="170"
          fixed="left"
          key="affName"
          v-if="groupItemCheck.affiliateCheck"
        >
          <template slot-scope="scope">
            <span class="cor337ab7" @click="affiliateDetailClick(scope.row)">{{
              scope.row.affName
            }}</span>
          </template>
        </el-table-column>
        <el-table-column
          label="Company Platform"
          prop="affCompanyPlatform"
          align="center"
          show-overflow-tooltip
          width="170"
          key="affCompanyPlatform"
          v-if="groupItemCheck.companyPlatformCheck"
        />
        <el-table-column
          label="Aff Entity"
          prop="affEntity"
          align="center"
          show-overflow-tooltip
          width="170"
          key="affEntity"
          v-if="groupItemCheck.affEntityCheck"
        />
        <el-table-column
          label="BD Name"
          prop="affBd"
          align="center"
          key="affBd"
          width="180"
          v-if="groupItemCheck.bdCheck"
        />
        <el-table-column
          label="AM Name"
          prop="affAm"
          align="center"
          key="affAm"
          width="180"
          v-if="groupItemCheck.amCheck"
        />
        <el-table-column
          label="Aff Revenue"
          prop="affRevenue"
          align="center"
          key="affRevenue"
          sortable="custom"
          width="150"
        />
        <el-table-column
          label="Aff Conversion"
          prop="affConversion"
          align="center"
          key="affConversion"
          sortable="custom"
          width="160"
        />
        <el-table-column
          label="Aff Deduction Amount"
          prop="affDeductionAmount"
          align="center"
          key="affDeductionAmount"
          sortable="custom"
          width="200"
        />
        <el-table-column
          label="Aff Cover Amount"
          prop="affCoverAmount"
          align="center"
          key="affCoverAmount"
          sortable="custom"
          width="200"
        />
        <el-table-column
          label="Aff Hedge Amount"
          prop="affHedgeAmount"
          align="center"
          key="affHedgeAmount"
          sortable="custom"
          width="200"
        />
        <el-table-column
          label="Aff Confirmed Amount"
          prop="affConfirmedAmount"
          align="center"
          key="affConfirmedAmount"
          sortable="custom"
          width="220"
        />
        <el-table-column
          label="Settlement Rate"
          prop="settlementRate"
          align="center"
          key="settlementRate"
          sortable="custom"
          width="180"
        >
          <template #default="scope">
            {{ numToPercentStr(scope.row.settlementRate) }}
          </template>
        </el-table-column>
        <el-table-column
          label="Payment Status"
          prop="paymentStatus"
          align="center"
          key="paymentStatus"
          width="150"
          v-if="groupItemCheck.paymentStatusCheck"
        >
          <template #default="scope">
            {{ getEnumLabel(affHedgeAppPaymentStatusList, scope.row.paymentStatus) }}
          </template>
        </el-table-column>
        <el-table-column
          label="Actual Amount"
          prop="actualAmount"
          align="center"
          key="actualAmount"
          sortable="custom"
          width="150"
        />
        <el-table-column
          label="Payment Amount"
          prop="financePaymentAmount"
          align="center"
          key="financePaymentAmount"
          sortable="custom"
          width="180"
        />
        <el-table-column
          label="Reconciliation Progress"
          prop="reconciliationProgress"
          align="center"
          key="reconciliationProgress"
          width="190"
          v-if="groupItemCheck.reconciliationProgressCheck"
        >
          <template #default="scope">
            {{ getEnumLabel(reconciliationProgressList, scope.row.reconciliationProgress) }}
          </template>
        </el-table-column>
        <el-table-column
          label="Billing Sending Status"
          prop="affBillingAppStatus"
          align="center"
          key="affBillingAppStatus"
          width="210"
        >
          <template #default="scope">
            {{ getEnumLabel(affBillingAppStatusList, scope.row.affBillingAppStatus) }}
          </template>
        </el-table-column>
        <el-table-column
          label="Payment Time"
          prop="paymentTime"
          align="center"
          key="paymentTime"
          sortable="custom"
          width="160"
        />
        <el-table-column
          label="Create Time"
          prop="createTime"
          align="center"
          key="createTime"
          width="160"
        />
        <el-table-column
          label="Create By"
          prop="createBy"
          align="center"
          key="createBy"
          width="160"
        />
      </el-table>
    </template>
    <template #pagination>
      <pagination
        class="block pagePanel"
        :pageTotal="total"
        @handleSizeChange="handleSizeChange"
        @handleCurrentChange="handleCurrentChange"
        :pageSize="filter.pageSize"
        :currentPage="filter.page"
      />
    </template>
    <template #dialog>
      <affiliate-billing-base-form
        :dialogVisible.sync="dialogVisible"
        :dialogType="dialogType"
        :affiliatesGroupList="affiliatesGroupList"
        :reconciliationProgressList="reconciliationProgressList"
        :allBdList="allBdList"
        :allAmList="allAmList"
        :affPaymentTypeList="affPaymentTypeList"
        :affHedgeAppPaymentStatusList="affHedgeAppPaymentStatusList"
        :affPaymentMergeStatusList="affPaymentMergeStatusList"
        :editData="editData"
        @refresh-list="handleSearch"
      />
    </template>
  </base-list-layout>
</template>

<style scoped lang="scss"></style>
